import React, { useState } from "react";
import { auth, db } from "../firebase-config";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { updateProfile } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import "../styles/editprofile.css";

const EditProfile = () => {
  const [displayName, setDisplayName] = useState(auth.currentUser?.displayName || "");
  const [photoURL, setPhotoURL] = useState(auth.currentUser?.photoURL || "");
  const [upiId, setUpiId] = useState(""); // Optional UPI ID
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false); // Loading state
  const navigate = useNavigate();

  const handleImageCompression = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          const MAX_WIDTH = 50;
          const MAX_HEIGHT = 50;

          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height = Math.round((MAX_WIDTH / width) * height);
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = Math.round((MAX_HEIGHT / height) * width);
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;

          ctx.drawImage(img, 0, 0, width, height);

          const compressedData = canvas.toDataURL("image/jpeg", 0.7);
          resolve(compressedData);
        };
        img.onerror = reject;
        img.src = event.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const compressedImage = await handleImageCompression(file);
        setPhotoURL(compressedImage);
      } catch (error) {
        console.error("Error compressing image:", error);
        setError("Failed to process the image.");
      }
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    const userId = auth.currentUser?.uid;

    if (!userId) {
      setError("User not logged in.");
      return;
    }

    setLoading(true); // Start loading

    try {
      // Update Firebase Auth Profile
      await updateProfile(auth.currentUser, {
        displayName,
        photoURL,
      });

      // Update Firestore User Document
      const userRef = doc(db, "users", userId);
      const docSnap = await getDoc(userRef);

      if (!docSnap.exists()) {
        setError("User profile does not exist.");
        return;
      }

      // Update Firestore with optional UPI ID
      const updateData = {
        displayName,
        photoURL,
      };
      if (upiId.trim() !== "") {
        updateData.upiId = upiId;
      }

      await updateDoc(userRef, updateData);

      setSuccess("Profile updated successfully!");
      setError(null);
      navigate("/dashboard");
    } catch (err) {
      console.error("Error updating profile:", err);
      setError("Failed to update profile. Please try again.");
      setSuccess(null);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  return (
    <div className="edit-profile-container">
      <h2>Edit Profile</h2>

      {loading ? (
        <div className="loading-spinner">
          <p>Updating profile, please wait...</p>
        </div>
      ) : (
        <form onSubmit={handleUpdateProfile}>
          <div className="form-group">
            <label htmlFor="displayName">Display Name</label>
            <input
              type="text"
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="photoUpload">Upload Profile Photo</label>
            <input
              type="file"
              id="photoUpload"
              accept="image/*"
              onChange={handleFileChange}
            />
          </div>

          {photoURL && (
            <div className="preview-container">
              <img src={photoURL} alt="Profile Preview" className="profile-preview" />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="upiId">UPI ID (Optional)</label>
            <input
              type="text"
              id="upiId"
              placeholder="Give UPI ID to accept payments"
              value={upiId}
              onChange={(e) => setUpiId(e.target.value)}
            />
          </div>

          <button type="submit" className="button">Save Changes</button>
        </form>
      )}

      {error && <p className="error-message">{error}</p>}
      {success && <p className="success-message">{success}</p>}

      <button className="button" onClick={() => navigate("/dashboard")}>
        Cancel
      </button>
    </div>
  );
};

export default EditProfile;
